import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { NotificationBadge, Tabs } from '@frontend/design-system';
import { LocationAccordionList } from '../../../../components/location-accordion-list';
import { useScheduleActionsContainerMethod } from '../../../../context/ScheduleActionsContainerContext';
import { useIsRequestTabEnabled } from '../../../../hooks/flags/use-is-request-tab-enabled';
import { SchedulePulseTab, useSchedulePulseShallowStore } from '../../../../stores/schedule-pulse.store';
import { InsuranceTabContent } from './insurance-tab-content';
import { useScheduleActionsContext } from './ScheduleActionsContext';
import { ScheduleAlerts } from './ScheduleAlerts';
import { ScheduleMassMessagesV2 } from './ScheduleMassMessages/schedule-mass-messages-v2';
import { ScheduleMassMessagesV3 } from './ScheduleMassMessagesV3';
import { useOpenedRequestLocation } from './ScheduleRequests/hooks/use-opened-request-location';
import { SchedulePulseRequestList } from './ScheduleRequests/ScheduleRequestList/SchedulePulseRequestList/SchedulePulseRequestList';
import { ScheduleRequestTabHeader } from './ScheduleRequests/ScheduleRequestTabHeader';

export const ScheduleActionsTabContent = () => {
  const { t } = useTranslation('schedule-pulse');
  const [controlledTab, setControlledTab] = useState<SchedulePulseTab>(SchedulePulseTab.AppointmentList);
  const { scheduleAlertsTotalCount, scheduleRequestsCountByLocation, isAppointmentRequestCountLoading } =
    useScheduleActionsContext();
  const { isSmallModeWindow } = useScheduleActionsContainerMethod();
  const { selectedTab: storeSelectedTab, setSelectedTab: setStoreSelectedTab } = useSchedulePulseShallowStore(
    'selectedTab',
    'setSelectedTab'
  );

  const { getScopeName } = useAppScopeStore();
  const { isRequestTabEnabled, enabledLocationIds: onlineSchedulingEnabledLocationIds } = useIsRequestTabEnabled();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasScheduleAlertsAccess = getFeatureFlagValue('schedule:alerts', 'ANY');

  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const { hasInsuranceVerificationAccess } = useHasWIVOrEIVAccess();

  const shouldShowInsuranceStatusTab = hasInsuranceVerificationAccess;

  const scheduleRequestLocationInfoList = useMemo(() => {
    if (!isRequestTabEnabled) return [];

    // filter locations that have scheduling flag enabled
    return onlineSchedulingEnabledLocationIds.map((locationId) => ({
      locationId,
      name: getScopeName(locationId),
      count: scheduleRequestsCountByLocation.find((item) => item.locationId === locationId)?.total ?? 0,
    }));
  }, [isRequestTabEnabled, onlineSchedulingEnabledLocationIds, getScopeName, scheduleRequestsCountByLocation]);

  const scheduleRequestTotalCount = scheduleRequestLocationInfoList.reduce((acc, { count }) => acc + count, 0);

  useEffect(() => {
    const selectedTab =
      (!isRequestTabEnabled && storeSelectedTab === SchedulePulseTab.AppointmentRequests
        ? SchedulePulseTab.AppointmentList
        : storeSelectedTab) || SchedulePulseTab.AppointmentList;

    setControlledTab(selectedTab);
  }, [isRequestTabEnabled, storeSelectedTab]);

  const handleTabChange = useCallback(
    (id: string) => setStoreSelectedTab(id as SchedulePulseTab),
    [setStoreSelectedTab]
  );

  const { selectedLocation, setSelectedLocation } = useOpenedRequestLocation();

  return (
    <Tabs controlledTab={controlledTab} onChange={handleTabChange}>
      <Tabs.Bar css={{ padding: theme.spacing(0, 3) }}>
        <Tabs.Tab trackingId={`${SchedulePulseTab.AppointmentList}-panel`} id={SchedulePulseTab.AppointmentList}>
          {t('Appointment List')}
        </Tabs.Tab>
        {isRequestTabEnabled && (
          <Tabs.Tab
            trackingId={`${SchedulePulseTab.AppointmentRequests}-panel`}
            id={SchedulePulseTab.AppointmentRequests}
          >
            <ScheduleRequestTabHeader count={scheduleRequestTotalCount} isLoading={isAppointmentRequestCountLoading} />
          </Tabs.Tab>
        )}
        {shouldShowInsuranceStatusTab && (
          <Tabs.Tab trackingId={`${SchedulePulseTab.Insurance}-panel`} id={SchedulePulseTab.Insurance}>
            {t('Insurance')}
          </Tabs.Tab>
        )}
        {hasScheduleAlertsAccess && (
          <Tabs.Tab trackingId={`${SchedulePulseTab.Alerts}-panel`} id={SchedulePulseTab.Alerts}>
            <span css={[tabStyles, { gap: theme.spacing(1) }]}>
              {t('Alerts')}
              {!!scheduleAlertsTotalCount && <NotificationBadge>{scheduleAlertsTotalCount}</NotificationBadge>}
            </span>
          </Tabs.Tab>
        )}
      </Tabs.Bar>
      <Tabs.Panel css={tabListPanelContainerStyles(true)} controller={SchedulePulseTab.AppointmentList}>
        {isScheduleV3FlagEnabled ? <ScheduleMassMessagesV3 /> : <ScheduleMassMessagesV2 />}
      </Tabs.Panel>
      {isRequestTabEnabled && (
        <Tabs.Panel
          css={tabListPanelContainerStyles(isSmallModeWindow)}
          controller={SchedulePulseTab.AppointmentRequests}
        >
          <LocationAccordionList
            locationInfoList={scheduleRequestLocationInfoList}
            ContentComponent={SchedulePulseRequestList}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            minBodyHeight={381}
          />
        </Tabs.Panel>
      )}
      {hasScheduleAlertsAccess && (
        <Tabs.Panel css={tabListPanelContainerStyles(isSmallModeWindow)} controller={SchedulePulseTab.Alerts}>
          <ScheduleAlerts />
        </Tabs.Panel>
      )}

      {shouldShowInsuranceStatusTab && (
        <Tabs.Panel css={[tabListPanelContainerStyles(true)]} controller={SchedulePulseTab.Insurance}>
          <InsuranceTabContent />
        </Tabs.Panel>
      )}
    </Tabs>
  );
};

const tabStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const tabListPanelContainerStyles = (isSmallWindow?: boolean) =>
  css({
    padding: theme.spacing(isSmallWindow ? 0 : 3),
    backgroundColor: theme.colors.neutral5,
    height: '100%',
    maxHeight: 'calc(100vh - 120px)', // Subtract approximate header height
    overflow: 'hidden',
  });
